import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import EngineerLayout from './EngineerLayout';
import { db } from '../../firebase';
import { ref, onValue, remove } from 'firebase/database';
import { 
    FaEdit, 
    FaTrash, 
    FaArrowLeft,
    FaSort,
    FaSearch,
    FaPrint,
    FaFileExcel,
    FaCopy,
    FaUserTie,
    FaMapMarkerAlt,
    FaRegFileAlt
} from 'react-icons/fa';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';

const EngineerExtendLimit = () => {
    const navigate = useNavigate();
    const [registrations, setRegistrations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: 'firstName', direction: 'asc' });

    // Marathi numerals for display
    const marathiNumerals = ['०', '१', '२', '३', '४', '५', '६', '७', '८', '९'];
    const toMarathiNumber = (n) => String(n).split('').map(digit => marathiNumerals[parseInt(digit)] || digit).join('');

    useEffect(() => {
        const regRef = ref(db, 'ekamwatapzp/EngineerRegistrations');
        const unsubscribe = onValue(regRef, (snapshot) => {
            if (snapshot.exists()) {
                const data = snapshot.val();
                const list = Object.entries(data).map(([key, val]) => ({
                    id: key,
                    ...val,
                    fullName: `${val.firstName || ''} ${val.surname || ''}`.trim(),
                    displayPhone: val.whatsappNo || val.mobile || "-",
                    regDate: val.submittedAt ? new Date(val.submittedAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : "-",
                    apprDate: val.approvedAt ? new Date(val.approvedAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : "-",
                    reuploadDate: val.reuploadDate ? new Date(val.reuploadDate).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : "-",
                }));
                setRegistrations(list.reverse());
            } else {
                setRegistrations([]);
            }
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const sortedData = useMemo(() => {
        let sortableItems = [...registrations];
        if (sortConfig !== null) {
            sortableItems.sort((a, b) => {
                const aValue = a[sortConfig.key] || '';
                const bValue = b[sortConfig.key] || '';
                if (aValue < bValue) {
                    return sortConfig.direction === 'asc' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortConfig.direction === 'asc' ? 1 : -1;
                }
                return 0;
            });
        }
        return sortableItems;
    }, [registrations, sortConfig]);

    const filteredData = useMemo(() => {
        return sortedData.filter(item => 
            (item.fullName && item.fullName.toLowerCase().includes(searchTerm.toLowerCase())) || 
            (item.whatsappNo && item.whatsappNo.includes(searchTerm))
        );
    }, [sortedData, searchTerm]);

    const handleDelete = async (id, name) => {
        if (window.confirm(`तुम्हाला खात्री आहे की तुम्हाला ${name} यांची नोंदणी हटवायची आहे?`)) {
            try {
                await remove(ref(db, `ekamwatapzp/EngineerRegistrations/${id}`));
                toast.success("नोंदणी यशस्वीरीत्या हटवली गेली!");
            } catch (error) {
                console.error("Delete error:", error);
                toast.error("नोंदणी हटवताना त्रुटी आली.");
            }
        }
    };

    const downloadExcel = () => {
        const excelData = filteredData.map((item, index) => ({
            '#': index + 1,
            'Applicant Name': item.fullName,
            'Phone No.': item.whatsappNo,
            'Reg. Date': item.regDate,
            'Appr. / Rej Date': item.apprDate,
            'Reupload Date': item.reuploadDate,
            'Add. CEO Status': item.addCEOStatus || 'Pending',
            'Ex. Engineer Status': item.exEngineerStatus || 'Pending',
            'Sr. Accountant Status': item.srAccountantStatus || 'Pending',
            'Jr. / Sr. Assistant Status': item.jrSrAssistantStatus || 'Pending',
        }));

        const ws = XLSX.utils.json_to_sheet(excelData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Limit_Expand_Apps");
        XLSX.writeFile(wb, `Limit_Expand_Apps_${new Date().toLocaleDateString()}.xlsx`);
    };

    const copyToClipboard = () => {
        const headers = ['#', 'Applicant Name', 'Phone No.', 'Reg. Date', 'Add. CEO Status'];
        const rows = filteredData.map((item, index) => [
            index + 1,
            item.fullName,
            item.displayPhone,
            item.regDate,
            item.addCEOStatus || 'Pending'
        ]);

        const content = [headers, ...rows].map(row => row.join('\t')).join('\n');
        navigator.clipboard.writeText(content);
        toast.success("माहिती कॉपी झाली!");
    };

    const getStatusText = (status) => {
        const isApproved = status === "Approved" || status === "मंजूर" || status === "Accepted";
        return (
            <span className={`text-[12px] font-bold ${isApproved ? 'text-emerald-500' : 'text-amber-500'}`}>
                {status || "Pending"}
            </span>
        );
    };

    if (loading) {
        return (
            <EngineerLayout>
                <div className="min-h-screen flex items-center justify-center bg-[#f4f7fe]">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
                </div>
            </EngineerLayout>
        );
    }

    return (
        <EngineerLayout>
            <div className="p-4 md:p-8 bg-[#f4f7fe] min-h-screen font-sans">
                {/* Header Section */}
                <div className="flex justify-between items-center mb-6 px-2 no-print">
                    <div className="flex items-center gap-4">
                        <h1 className="text-xl md:text-2xl font-bold text-gray-700 tracking-tight">लिमिट एक्सपांड ॲप्लिकेशन</h1>
                    </div>
                    <div className="text-[12px] font-medium text-gray-500 font-marathi">
                        सांगली जिल्हा परिषद / <span className="text-gray-600">व्हिव लिमिट एक्सपांड फॉर्म</span>
                    </div>
                </div>

                {/* Content Card */}
                <div className="bg-white rounded shadow-sm border border-gray-100 p-6">
                    {/* Toolbar */}
                    <div className="flex flex-col md:flex-row justify-between items-center gap-4 mb-8 no-print">
                        <div className="flex items-center">
                            <div className="flex bg-[#6c757d] rounded shadow-sm overflow-hidden">
                                <button onClick={copyToClipboard} className="hover:bg-[#5a5a5a] text-white px-5 py-2 text-sm font-medium transition border-r border-gray-500/50">
                                    Copy
                                </button>
                                <button onClick={downloadExcel} className="hover:bg-[#5a5a5a] text-white px-5 py-2 text-sm font-medium border-r border-gray-500/50 transition">
                                    Excel
                                </button>
                                <button onClick={() => window.print()} className="hover:bg-[#5a5a5a] text-white px-5 py-2 text-sm font-medium transition">
                                    Print
                                </button>
                            </div>
                        </div>
                        <div className="flex items-center gap-3 w-full md:w-auto">
                            <label className="text-sm font-medium text-gray-500">Search:</label>
                            <input 
                                type="text" 
                                className="border border-gray-200 rounded px-3 py-1.5 text-sm outline-none focus:border-blue-400 transition-all bg-white w-full md:w-64"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>

                    {/* Table Container */}
                    <div className="border border-gray-100 rounded overflow-hidden overflow-x-auto">
                        <table className="w-full border-collapse">
                            <thead className="bg-[#f8f9fc] border-b border-gray-100 uppercase tracking-tighter">
                                <tr>
                                    <th className="px-4 py-4 text-[11px] font-bold text-gray-800 text-center w-12 border-r border-gray-100">
                                        <div className="flex items-center justify-center gap-1 cursor-pointer" onClick={() => handleSort('id')}># <FaSort className="opacity-20" /></div>
                                    </th>
                                    <th className="px-4 py-4 text-[11px] font-bold text-gray-800 text-left min-w-[180px] border-r border-gray-100">
                                        <div className="flex items-center gap-1 cursor-pointer" onClick={() => handleSort('fullName')}>Applicant Name <FaSort className="opacity-20" /></div>
                                    </th>
                                    <th className="px-4 py-4 text-[11px] font-bold text-gray-800 text-left w-36 border-r border-gray-100">
                                        <div className="flex items-center gap-1 cursor-pointer" onClick={() => handleSort('whatsappNo')}>Phone No. <FaSort className="opacity-20" /></div>
                                    </th>
                                    <th className="px-4 py-4 text-[11px] font-bold text-gray-800 text-left w-28 border-r border-gray-100">
                                        <div className="flex items-center gap-1 cursor-pointer" onClick={() => handleSort('submittedAt')}>Reg. Date <FaSort className="opacity-20" /></div>
                                    </th>
                                    <th className="px-4 py-4 text-[11px] font-bold text-gray-800 text-left w-28 border-r border-gray-100">
                                        <div className="flex items-center gap-1 cursor-pointer">Appr. / Rej Date <FaSort className="opacity-20" /></div>
                                    </th>
                                    <th className="px-4 py-4 text-[11px] font-bold text-gray-800 text-left w-32 border-r border-gray-100">
                                        <div className="flex items-center gap-1 cursor-pointer">Reupload Date <FaSort className="opacity-20" /></div>
                                    </th>
                                    <th className="px-4 py-4 text-[11px] font-bold text-gray-800 text-left w-28 border-r border-gray-100">
                                        <div className="flex items-center gap-1 cursor-pointer">Add. CEO Status <FaSort className="opacity-20" /></div>
                                    </th>
                                    <th className="px-4 py-4 text-[11px] font-bold text-gray-800 text-left w-28 border-r border-gray-100">
                                        <div className="flex items-center gap-1 cursor-pointer">Ex. Engineer Status <FaSort className="opacity-20" /></div>
                                    </th>
                                    <th className="px-4 py-4 text-[11px] font-bold text-gray-800 text-left w-28 border-r border-gray-100">
                                        <div className="flex items-center gap-1 cursor-pointer">Sr. Accountant Status <FaSort className="opacity-20" /></div>
                                    </th>
                                    <th className="px-4 py-4 text-[11px] font-bold text-gray-800 text-left w-32 border-r border-gray-100">
                                        <div className="flex items-center gap-1 cursor-pointer">Jr. / Sr. Assistant Status <FaSort className="opacity-20" /></div>
                                    </th>
                                    <th className="px-4 py-4 text-[11px] font-bold text-gray-800 text-center w-24">
                                        <div className="flex items-center justify-center gap-1 cursor-pointer">Action <FaSort className="opacity-20" /></div>
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                                {filteredData.length > 0 ? filteredData.map((item, index) => (
                                    <tr key={item.id} className={`${index % 2 === 0 ? 'bg-white' : 'bg-[#f4f7fe]/40'} hover:bg-blue-50/50 transition-colors text-[13px] text-gray-700 font-medium`}>
                                        <td className="px-4 py-4 text-center border-r border-gray-50">{index + 1}</td>
                                        <td className="px-4 py-4 border-r border-gray-50">
                                            <div className="uppercase leading-tight max-w-[160px] truncate">{item.fullName}</div>
                                        </td>
                                        <td className="px-4 py-4 border-r border-gray-50">{item.displayPhone}</td>
                                        <td className="px-4 py-4 border-r border-gray-50 whitespace-nowrap">{item.regDate}</td>
                                        <td className="px-4 py-4 border-r border-gray-50 whitespace-nowrap">{item.apprDate}</td>
                                        <td className="px-4 py-4 border-r border-gray-50 text-[11px]">{item.reuploadDate}</td>
                                        <td className="px-4 py-4 border-r border-gray-50">
                                            {getStatusText(item.addCEOStatus)}
                                        </td>
                                        <td className="px-4 py-4 border-r border-gray-50">
                                            {getStatusText(item.exEngineerStatus)}
                                        </td>
                                        <td className="px-4 py-4 border-r border-gray-50">
                                            {getStatusText(item.srAccountantStatus)}
                                        </td>
                                        <td className="px-4 py-4 border-r border-gray-50">
                                            {getStatusText(item.jrSrAssistantStatus)}
                                        </td>
                                        <td className="px-4 py-4 text-center no-print">
                                            <div className="flex items-center justify-center gap-3">
                                                <button 
                                                    onClick={() => navigate(`/engineer/extend-limit/edit/${item.id}`)}
                                                    className="text-gray-400 hover:text-blue-500 transition"
                                                    title="View/Edit"
                                                >
                                                    <FaRegFileAlt size={16} />
                                                </button>
                                                <button 
                                                    onClick={() => handleDelete(item.id, item.fullName)}
                                                    className="text-gray-400 hover:text-red-500 transition"
                                                    title="Delete"
                                                >
                                                    <FaTrash size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                )) : (
                                    <tr>
                                        <td colSpan="11" className="px-6 py-20 text-center text-gray-400 font-bold italic bg-gray-50/30 tracking-widest uppercase">
                                            कोणतीही माहिती उपलब्ध नाही.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Footer Copy */}
                <div className="mt-8 text-center text-[10px] text-gray-400 font-sans tracking-widest uppercase no-print">
                    Owned and Operated by ZP SANGLI. Copyright © 2026. All Rights Reserved.
                </div>
            </div>

            <style>{`
                .font-marathi {
                    font-family: 'Hind', sans-serif;
                }
                @import url('https://fonts.googleapis.com/css2?family=Hind:wght@400;500;600;700&display=swap');
                @media print {
                    .no-print { display: none !important; }
                    body { background: white !important; }
                    .shadow-sm, .shadow-md { box-shadow: none !important; }
                    .rounded-2xl { border-radius: 0 !important; }
                    .p-8 { padding: 0 !important; }
                    table { border: 1px solid #efefef !important; width: 100% !important; font-size: 10px !important; }
                    th, td { padding: 8px 4px !important; }
                }
                thead th {
                    white-space: normal;
                    line-height: 1.2;
                }
            `}</style>
        </EngineerLayout>
    );
};

export default EngineerExtendLimit;
